import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { fetchCircleMemberships } from '../../store/actions/circleActions';
import AthleteEditHub from '../ForAthletes/v2/edit/AthleteEditHub';
import { Btn, EmptyState, Loading, PageHeader } from '../ForBrands/ui/BrandUI';

// Circle-member entry point for editing an athlete's profile: the V2 edit
// hub for the athlete whose email CirclePage or SupporterCirclePage put in
// localStorage ("circleAthleteEmail") when "Edit profile" was clicked. The
// page body only: the supporter shell or the Player Index shell draws the
// frame. The hub loads the athlete by that email and saves through the
// standard athlete/update/ call, where the server enforces the member's
// Circle permission. The member's own membership row gives the permission
// the header shows; a "view" member is told the profile cannot be edited.
//
// The `key` forces a full remount when the target changes, same reason as
// InternPlayerProfileEdit: the reconciler otherwise preserves the previous
// athlete's editor state.

const EMPTY = Object.freeze([]);
const sameEmail = (x, y) => String(x || '').trim().toLowerCase() === String(y || '').trim().toLowerCase();

// The member's Circle home: the supporter shell's page for a supporter (or
// anyone already in that shell), /circle for everyone else.
export const circleHomeFor = (pathname, permissions) => (
  String(pathname || '').startsWith('/supporter') || (permissions.includes('IS_SUPPORTER') && !permissions.includes('IS_ADMIN'))
    ? '/supporter/circle'
    : '/circle'
);

const CircleAthleteEdit = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const permissions = useSelector((s) => s.user?.user?.permissions || EMPTY);
  const editingEmail = localStorage.getItem('circleAthleteEmail') || '';
  const home = circleHomeFor(pathname, permissions);
  // undefined while loading, null when the row is missing or the list did
  // not load (the server still decides every save).
  const [membership, setMembership] = useState(undefined);

  useEffect(() => {
    if (!editingEmail) return undefined;
    let live = true;
    setMembership(undefined);
    Promise.resolve(dispatch(fetchCircleMemberships()))
      .then((list) => {
        if (live) setMembership((Array.isArray(list) ? list : []).find((m) => sameEmail(m?.athleteEmail, editingEmail)) || null);
      })
      .catch(() => { if (live) setMembership(null); });
    return () => { live = false; };
  }, [dispatch, editingEmail]);

  if (!editingEmail) return <Navigate to={home} replace />;
  if (membership === undefined) return <Loading label="Loading the profile..." />;

  if (membership?.permission === 'view') {
    const name = membership.athleteName || 'This athlete';
    return (
      <div>
        <PageHeader eyebrow="Circle" title="View only" subtitle={`${name} gave you View only access in their Circle, so the profile cannot be edited from your account.`} />
        <EmptyState
          action={(
            <div className="flex flex-wrap justify-center gap-3">
              {membership.athleteId && <Btn variant="secondary" to={`/p/${membership.athleteId}`}>View profile</Btn>}
              <Btn to={home}>Back to My Circle</Btn>
            </div>
          )}
        >
          Ask {membership.athleteName || 'the athlete'} to change your access if you should be able to edit it.
        </EmptyState>
      </div>
    );
  }

  return (
    <AthleteEditHub
      key={`${pathname}:${editingEmail}`}
      editingEmail={editingEmail}
      circlePermission={membership?.permission || ''}
      backTo={home}
    />
  );
};

export default CircleAthleteEdit;
